import React, { memo, useContext, useEffect } from 'react'
import { StyledSaveSearchButton } from '../../components/save-search/save-a-search/save-search-button.style'
import { IconPosition } from '@at/design-system-elements'
import { SaveSearchModal } from '../../components/save-search/save-a-search/save-search-modal'
import { useAfterSignInUrl } from '../../hooks/after-signin-url-hook'
import { isTpf } from '@at/formatter-utils/lib/channel-utils/channel.util'
import { SavedSearchQueryContext } from '../../contexts/saved-search-context'
import { dataAttrTestId } from '@at/formatter-utils/lib/data-attr-utils/data-attr-utils'
import { IconSavedOff, IconSavedOn } from '@at/design-system-icons'
import { PortalComponent } from '../../components/modals/portal'
import { useATTrackInteraction } from '@at/consumer-web-snowplow'
import { useUserState } from '@at/connected-signin'
import { useIsLeasingSearch } from '../../utils/is-leasing-search'
import { useSearchViewState } from '../../contexts/search-view-state-context'
import { ModalLoginContextProvider } from '../../contexts/modal-login-context'
import { useChannel } from '../../hooks/use-abtest.hook'

export interface CanonicalSearchRangeValues {
    from?: string
    to?: string
}

export interface CanonicalSearchFinanceOption {
    financeDeposit: string
    financeTerm: string
    financeYearlyMileage: string
}

export interface CanonicalSearchValues {
    [key: string]: string[] | CanonicalSearchRangeValues | CanonicalSearchFinanceOption
}

export interface SavedSearch {
    id: string
    name: string
    canonicalSearchValues: CanonicalSearchValues
    searchCriteria: string
}

export interface PersonalData {
    email: string | undefined
    verified: boolean | undefined
}

interface SaveSearchProps {
    forceOpen?: boolean
}

const SaveSearchCTA = ({ forceOpen = false }: SaveSearchProps) => {
    const [userState] = useUserState()
    const { showSaveThisSearch, setShowSaveThisSearch } = useSearchViewState()
    const isLeasingSearch = useIsLeasingSearch()
    const handleTrackingInteraction = useATTrackInteraction({
        trackingLabel: 'save-search-signed-in',
    })
    const { savedSearchListRefetch, foundMatchingSavedSearch } = useContext(SavedSearchQueryContext)
    const channel = useChannel()

    const { saveASearchAfterSignInUrl } = useAfterSignInUrl('SAVE_SEARCH')

    useEffect(() => {
        if (forceOpen) {
            setShowSaveThisSearch(true)
        }
    }, [forceOpen, setShowSaveThisSearch])

    const handleCloseModal = () => {
        setShowSaveThisSearch(false)
        savedSearchListRefetch()
    }

    const savedSearchLoggedIn = () => {
        handleTrackingInteraction()
        setShowSaveThisSearch(true)
    }

    const savedSearchLoggedOut = () => (window.location.href = saveASearchAfterSignInUrl)
    const saveSearchButtonText = foundMatchingSavedSearch ? 'Search saved' : 'Save search'

    const SaveIcon = foundMatchingSavedSearch ? IconSavedOn : IconSavedOff

    if (isLeasingSearch) {
        return null
    }

    return (
        <ModalLoginContextProvider trigger="SAVE_SEARCH">
            {!isTpf(channel) && (
                <StyledSaveSearchButton
                    type={'button'}
                    displayType="tertiary"
                    text={saveSearchButtonText}
                    onInteraction={() => (userState.userSignedIn ? savedSearchLoggedIn() : savedSearchLoggedOut())}
                    iconSrc={<SaveIcon title="Save search icon" customSize={{ height: 16, width: 16 }} />}
                    iconPosition={IconPosition.RIGHT}
                    $foundMatchingSavedSearch={foundMatchingSavedSearch}
                    attributes={{
                        [`${dataAttrTestId}`]: 'save-search-button',
                        'aria-label': saveSearchButtonText,
                    }}
                />
            )}
            <PortalComponent show={showSaveThisSearch}>
                <SaveSearchModal handleHideModal={() => handleCloseModal()} />
            </PortalComponent>
        </ModalLoginContextProvider>
    )
}

export const SaveSearch = memo(SaveSearchCTA)
